import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

// ===============================
// 🔧 CONFIGURAÇÃO DA URL BASE
// ===============================

// Novo backend (Render)
const renderUrl = 'https://psnr-ordem-servico.onrender.com/';

// Usa a variável de ambiente, se existir, ou o Render como fallback
const baseURL =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  renderUrl;

// Log apenas em desenvolvimento
if (process.env.NODE_ENV === 'development') {
  console.log('🔧 API Base URL:', baseURL);
}

// ===============================
// 🚀 INSTÂNCIA DO AXIOS
// ===============================
const api = axios.create({
  baseURL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// ===============================
// 🔐 INTERCEPTOR DE REQUISIÇÃO
// ===============================
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('access');
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Logs de debug (opcional, útil p/ diagnóstico)
    console.log('📤 REQUEST:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url}`,
      headers: config.headers,
      origin: window.location.origin,
    });

    return config;
  },
  (error) => {
    console.error('❌ REQUEST ERROR:', error);
    return Promise.reject(error);
  }
);

// ===============================
// ⚙️ INTERCEPTOR DE RESPOSTA
// ===============================
api.interceptors.response.use(
  (response) => {
    console.log('✅ RESPONSE:', {
      status: response.status,
      statusText: response.statusText,
      url: response.config.url,
      headers: response.headers,
      data: response.data,
    });
    return response;
  },
  (error: AxiosError) => {
    console.error('❌ RESPONSE ERROR:', {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      baseURL: error.config?.baseURL,
      fullURL: error.config ? `${error.config.baseURL}${error.config.url}` : 'N/A',
      responseHeaders: error.response?.headers,
      requestHeaders: error.config?.headers,
      responseData: error.response?.data,
      origin: window.location.origin,
    });

    // Erros de rede (sem resposta)
    if (!error.response) {
      console.error('❌ Erro de rede (sem resposta do servidor):', error.message);
      console.error('🔍 Verificações:');
      console.error('  - Backend URL:', baseURL);
      console.error('  - Origin:', window.location.origin);
      console.error('  - Request URL:', error.config ? `${error.config.baseURL}${error.config.url}` : 'N/A');
    } else {
      // Erro HTTP com resposta
      console.error('❌ Erro HTTP:', {
        status: error.response.status,
        statusText: error.response.statusText,
        headers: error.response.headers,
        data: error.response.data,
      });

      // Checagem de CORS
      const corsHeaders = {
        'access-control-allow-origin': error.response.headers['access-control-allow-origin'],
        'access-control-allow-methods': error.response.headers['access-control-allow-methods'],
        'access-control-allow-headers': error.response.headers['access-control-allow-headers'],
      };
      console.error('🔍 CORS Headers na resposta:', corsHeaders);

      if (!corsHeaders['access-control-allow-origin']) {
        console.error('⚠️ ATENÇÃO: Header Access-Control-Allow-Origin não está presente na resposta!');
        console.error('   Isso indica que o CORS não está configurado corretamente no backend.');
      }
    }

    // Erros de autenticação
    if (error.response?.status === 401) {
      // Não remove token nem redireciona se estiver na página de login
      // (permite que a página de login trate o erro adequadamente)
      const isLoginPage = window.location.pathname === '/' || window.location.pathname === '/login';
      if (!isLoginPage) {
        localStorage.removeItem('access');
        window.location.href = '/';
      }
    }

    return Promise.reject(error);
  }
);

export default api;